import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { hueOf } from '../../lib/initials';
import PersonCircle from '../Avatar';
import { Icon } from './icons';
import './TodoShare.css';

export { PeopleIcon, TodoPlusIcon } from './icons';

/**
 * PEOPLE IN THE TO-DO LIST (batch 4: share and assign).
 *
 * Gordon: shared lists, giving a to-do to somebody, comments on an item, and
 * a to-do made from a chat message or a ticket. Everything here is about the
 * people in that sentence - their faces, finding them, a list shared with
 * them, a to-do sent to them - and none of it decides who may be reached.
 * The people offered are the chat directory (/chat/people), which is already
 * exactly the set the server lets this person reach; the server refuses
 * anybody else regardless, so offering only those means nobody is shown a
 * choice that would then be turned down.
 *
 * A person is named by the key the chat uses: 'u:<uuid>' for staff,
 * 'c:<uuid>' for a contractor. One shape for the directory, a list's
 * members and a to-do's assignee, so nothing is translated on the way.
 */

/** The key of a directory row, which an older server may send without one. */
export const personKey = (p) => (p && (p.key
  || (p.kind && p.id ? `${p.kind === 'contractor' ? 'c' : 'u'}:${p.id}` : null))) || null;

/** Whether somebody is a contractor - IT, to everybody in the firm. */
export const isContractor = (p) => Boolean(p && (p.is_contractor === true
  || p.kind === 'contractor' || String(p.key || '').startsWith('c:')));

/**
 * Their photo, or initials on a tint (components/Avatar.js - the one circle
 * every screen draws). The tint comes from the key, as chat's does (hueOf),
 * so Marco is the same colour on a row here as beside his messages - and the
 * same face, once he has chosen one.
 */
export function Avatar({ name, who, label = null, size = 'sm' }) {
  const a11y = label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true };
  return (
    <PersonCircle person={{ key: who, name }} className="tds-av" data-size={size}
                  data-hue={hueOf(who || name)} title={label || name || undefined} {...a11y} />
  );
}

/** "IT", beside a contractor's name. */
export const ItTag = () => <span className="tds-it" title="A contractor">IT</span>;

/**
 * Find one person in the directory. `people` null is still loading. Typing
 * narrows it by name; the first eight are offered, which on an
 * administrator's whole-firm directory is plenty once two letters are in.
 */
export function PeoplePicker({ people, exclude = null, onPick, label = 'Search people', autoFocus = false }) {
  const [q, setQ] = useState('');
  const pool = (people || []).map((p) => ({ ...p, key: personKey(p) }))
    .filter((p) => p.key && p.name && !(exclude && exclude.has(p.key)));
  const t = q.trim().toLowerCase();
  const hits = (t ? pool.filter((p) => String(p.name).toLowerCase().includes(t)) : pool).slice(0, 8);
  return (
    <div className="tds-pick">
      <input type="search" value={q} aria-label={label} placeholder="Search people…" autoFocus={autoFocus}
             onChange={(e) => setQ(e.target.value)} />
      {people === null ? (
        <p className="tds-quiet">Loading…</p>
      ) : hits.length === 0 ? (
        <p className="tds-quiet">{pool.length ? 'Nobody by that name.' : 'Nobody else you can reach.'}</p>
      ) : (
        <ul className="tds-pick-list">
          {hits.map((p) => (
            <li key={p.key}>
              <button type="button" className="tds-pick-item" data-person={p.key} onClick={() => onPick(p)}>
                <Avatar name={p.name} who={p.key} />
                <span className="tds-name">{p.name}</span>
                {isContractor(p) && <ItTag />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * A textarea that grows with what is typed, from `rows` lines up to the
 * max-height its stylesheet gives it (then it scrolls) - so an empty comment
 * box is one line, not a 110px well.
 */
export const GrowingTextarea = React.forwardRef(function GrowingTextarea({ value, ...rest }, ref) {
  const inner = useRef(null);
  const set = (el) => {
    inner.current = el;
    if (typeof ref === 'function') ref(el); else if (ref) ref.current = el;
  };
  useLayoutEffect(() => {
    const el = inner.current;
    if (!el) return;
    el.style.height = 'auto';
    // scrollHeight is the content and the padding; the 1px borders go on top.
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [value]);
  return <textarea ref={set} value={value} {...rest} />;
});

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), '
  + 'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const topmost = (el) => {
  const all = document.querySelectorAll('.tds-wrap');
  return all.length === 0 || all[all.length - 1] === el;
};

/**
 * A dialog near the top of the screen - a sheet along the foot of it on a
 * phone - over a dimmed page. Escape and a press on the scrim close it (the
 * top one only, when one is over another). It takes the focus when it opens
 * (whatever inside asked for it with autoFocus, else the first field, else
 * itself), keeps Tab inside it, and gives the focus back to whatever had it
 * when it closes. The page behind does not scroll while it is up.
 *
 * Anchored at the top, not centred, so a panel whose content changes height
 * (Comments to Activity) grows downwards instead of jumping about.
 *
 * `.tds-dialog` is also what tells the list's keyboard shortcuts to keep out
 * of the way while it is up.
 */
export function Dialog({ label, onClose, children, className = '' }) {
  const box = useRef(null);
  const wrap = useRef(null);
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    const back = document.activeElement;
    const el = box.current;
    if (el && !el.contains(document.activeElement)) {
      // Under a finger, a field taking the focus throws the keyboard up over
      // half the sheet: the dialog itself takes it there instead.
      let touch = false;
      try { touch = window.matchMedia('(hover: none)').matches; } catch { /* old browser */ }
      // Never a checkbox (a card popup's ring comes first in it): a stray
      // Space would tick the to-do.
      const first = touch ? null
        : el.querySelector('input:not([disabled]):not([type="hidden"]):not([type="checkbox"]), textarea:not([disabled])');
      (first || el).focus({ preventScroll: true });
    }
    const body = document.body;
    const was = body.style.overflow;
    body.style.overflow = 'hidden';
    return () => {
      body.style.overflow = was;
      if (back && typeof back.focus === 'function' && document.contains(back)) {
        try { back.focus({ preventScroll: true }); } catch { /* gone */ }
      }
    };
  }, []);

  useEffect(() => {
    // An Escape that closed the dialog in front of this one can reach the
    // document after this one has already been drawn in its place (React
    // flushes a key's updates and effects before the event bubbles on):
    // a key pressed before this listener existed is not for it.
    const born = performance.now();
    const onKey = (e) => {
      if (!topmost(wrap.current)) return;
      if (e.timeStamp && e.timeStamp <= born) return;
      if (e.key === 'Escape') { e.stopPropagation(); close.current(); return; }
      if (e.key !== 'Tab' || !box.current) return;
      const els = [...box.current.querySelectorAll(FOCUSABLE)].filter((x) => x.offsetParent !== null);
      if (!els.length) return;
      const first = els[0];
      const last = els[els.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === box.current)) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div ref={wrap} className="tds-wrap" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={box} className={`tds-dialog ${className}`} role="dialog" aria-modal="true" aria-label={label}
           tabIndex={-1}>
        <div className="tds-dialog-head">
          <h3 className="tds-dialog-title">{label}</h3>
          <button type="button" className="tds-x" aria-label="Close" onClick={onClose}>
            <Icon name="close" size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const ROLE = { owner: 'Owner', editor: 'Can edit' };

/**
 * SHARING A LIST. Who is in it, with their faces and what they may do; the
 * owner adds people from the directory and takes them off again, and
 * anybody else may leave. Everybody in a list sees everything in it and can
 * add, tick and comment - Todoist's shared projects, with no finer rights,
 * because a list of six jobs between two people does not need any.
 *
 * The page does the calls; this only says what was chosen.
 */
export function ShareDialog({ list, myKey, people, owner, busy, error, onAdd, onRemove, onLeave, onClose }) {
  const [adding, setAdding] = useState(false);
  const members = Array.isArray(list.members) ? list.members : [];
  const inList = new Set(members.map((m) => m.key));
  if (myKey) inList.add(myKey);
  return (
    <Dialog label={`Share "${list.name}"`} onClose={onClose} className="tds-share">
      {error && <p className="tds-error" role="alert">{error}</p>}
      {members.length === 0 ? (
        <p className="tds-quiet">Only you, so far. Everybody you add sees every to-do in it and can add, tick and comment.</p>
      ) : (
        <ul className="tds-members" aria-label="Members">
          {members.map((m) => (
            <li key={m.key} className="tds-member" data-member={m.key}>
              <Avatar name={m.name} who={m.key} />
              <span className="tds-name">
                {m.name}{m.key === myKey ? ' (you)' : ''}
              </span>
              {isContractor(m) && <ItTag />}
              <span className={`tds-role is-${m.role === 'owner' ? 'owner' : 'editor'}`}>{ROLE[m.role] || ROLE.editor}</span>
              {owner && m.key !== myKey && m.role !== 'owner' ? (
                <button type="button" className="tds-icon-btn is-danger tds-member-x" disabled={busy}
                        aria-label={`Remove ${m.name}`} title={`Remove ${m.name}`} onClick={() => onRemove(m)}>
                  <Icon name="close" size={16} />
                </button>
              ) : owner ? <span className="tds-member-x-space" aria-hidden="true" /> : null}
            </li>
          ))}
        </ul>
      )}
      {owner && (adding ? (
        <PeoplePicker people={people} exclude={inList} autoFocus label="Find somebody to add"
                      onPick={(p) => { setAdding(false); onAdd(p); }} />
      ) : (
        <button type="button" className="tds-add-people" disabled={busy}
                onClick={() => setAdding(true)}>
          <span className="tds-add-ring"><Icon name="plus" size={16} /></span>
          Add people
        </button>
      ))}
      <div className="tds-dialog-foot">
        {!owner && (
          <button type="button" className="tds-link is-danger" disabled={busy} onClick={onLeave}>Leave list</button>
        )}
        <button type="button" className="btn btn-primary tds-btn" onClick={onClose}>Done</button>
      </div>
    </Dialog>
  );
}

/**
 * SENDING A TO-DO to somebody - for a to-do in a list they are not in. It
 * leaves my lists for their Inbox, sub-tasks and all, and I keep sight of it
 * under Delegated. A note says why, if it needs saying.
 */
export function SendDialog({ item, people, preset = null, busy, error, onSend, onClose }) {
  const [to, setTo] = useState(preset);
  const [note, setNote] = useState('');
  return (
    <Dialog label={`Send "${item.title}"`} onClose={onClose} className="tds-send">
      {error && <p className="tds-error" role="alert">{error}</p>}
      <p className="tds-quiet">
        It goes into their Inbox, with its sub-tasks. You keep sight of it under Delegated.
      </p>
      {to ? (
        <p className="tds-chosen" data-chosen={to.key}>
          <Avatar name={to.name} who={to.key} />
          <span className="tds-name">{to.name}</span>
          {isContractor(to) && <ItTag />}
          <button type="button" className="tds-link" onClick={() => setTo(null)}>Change</button>
        </p>
      ) : (
        <PeoplePicker people={people} autoFocus label="Send to" onPick={setTo} />
      )}
      <label className="tds-field">
        <span>Note (optional)</span>
        <GrowingTextarea rows={2} maxLength={2000} value={note} aria-label="Note"
                         placeholder="Why, or anything they should know"
                         onChange={(e) => setNote(e.target.value)} />
      </label>
      <div className="tds-dialog-foot">
        <button type="button" className="btn btn-outline tds-btn" onClick={onClose}>Cancel</button>
        <button type="button" className="btn btn-primary tds-btn" disabled={busy || !to}
                onClick={() => onSend(to, note.trim())}>
          {to ? `Send to ${to.name.split(/\s+/)[0]}` : 'Send'}
        </button>
      </div>
    </Dialog>
  );
}

/** A few faces in a row, and "+3" for the rest - a shared list's header. */
export function Faces({ members, max = 3 }) {
  const list = Array.isArray(members) ? members : [];
  if (!list.length) return null;
  const shown = list.slice(0, max);
  return (
    <span className="tds-faces" role="img"
          aria-label={`Shared with ${list.map((m) => m.name).join(', ')}`}>
      {shown.map((m) => <Avatar key={m.key} name={m.name} who={m.key} />)}
      {list.length > max && <span className="tds-av tds-more">+{list.length - max}</span>}
    </span>
  );
}
